/**
 * Opciones para combinaciones de combos tipo "rolls-mixtos".
 * Textura, rolls, proteína de rolls especiales y complementos vienen del catálogo Xetux.
 */

/** Coincide con additionalName "FRIOS" del catálogo Xetux. */
export const TEXTURA_FRIOS = 'FRIOS';

/**
 * @param {string} name
 * @returns {boolean}
 */
export function isTexturaFrios(name) {
  const n = String(name || '')
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return n.includes('FRIO');
}

/** Valor de proteína «Mixtos» (regla especial en combos de 40/50 rolls, si existe en Xetux). */
export const PROTEINA_MIXTOS = 'MIXTOS';

/**
 * Rolls totales donde, si se elige «Mixtos» en proteína, aplica a todas las combinaciones.
 */
export const ROLLS_CON_REGLA_MIXTOS_GLOBAL = [40, 50];

/**
 * @param {string} name
 * @returns {boolean}
 */
export function isProteinaMixtos(name) {
  const n = String(name || '')
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return n === 'MIXTOS' || n.includes('MIXTO');
}

/**
 * En 10 ROLLS ESPECIALES estas proteínas no admiten dos complementos.
 * «CON TODO» no se ofrece cuando el cliente eligió una de ellas.
 */
export const PROTEINAS_SIN_CON_TODO = [
  'SOLO POLLO',
  'SOLO CAMARON',
  'SOLO ATUN',
  'SOLO CANGREJO',
  'SOLO ATUN CANGREJO',
  'ATUN CANGREJO',
];

export function proteinaBloqueaConTodo(name) {
  const n = normalizeComboName(name).replace(/[^A-Z0-9]+/g, ' ').trim();
  if (!n) return false;
  return PROTEINAS_SIN_CON_TODO.includes(n);
}

export function isComplementoConTodo(name) {
  return normalizeComboName(name).includes('CON TODO');
}

export function complementosParaProteina(complementos, proteina) {
  const list = Array.isArray(complementos) ? complementos : [];
  if (!proteinaBloqueaConTodo(proteina)) return list;
  return list.filter((name) => !isComplementoConTodo(name));
}

/** Roll por roll: solo plátano, aguacate o solo proteína. «CON TODO» queda para la combinación de la casa. */
export function complementosRollPorRoll(complementos) {
  const list = Array.isArray(complementos) ? complementos : [];
  return list.filter((name) => !isComplementoConTodo(name));
}

/** Pieza «10 ROLLS ESPECIALES»: pide un segundo select con el tipo (SOLO ATUN, etc.). */
export function isRollEspeciales(name) {
  const n = String(name || '')
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return n.includes('ESPECIAL');
}

/**
 * @param {unknown} options
 * @returns {{ id: number|null, name: string, sku: string }[]}
 */
export function mapRollProducts(options) {
  if (!Array.isArray(options)) return [];
  return options
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const name = String(item.name || '').trim();
      if (!name) return null;
      const id = Number(item.id);
      return {
        id: Number.isFinite(id) && id > 0 ? id : null,
        name,
        sku: String(item.sku || ''),
      };
    })
    .filter(Boolean);
}

/**
 * @param {{ textura?: string, roll?: string, proteina?: string, complemento?: string }} row
 * @returns {boolean}
 */
export function isCombinacionCompleta(row) {
  if (!row?.textura || !row?.roll || !row?.complemento) return false;
  if (isRollEspeciales(row.roll) && !row.proteina) return false;
  return true;
}

/**
 * Resumen visible en carrito y checkout.
 * @param {{ textura?: string, roll?: string, proteina?: string, complemento?: string }} combi
 * @returns {string}
 */
export function formatCombinacionSummary(combi) {
  const textura = combi?.textura || '';
  const roll = combi?.roll || '';
  const proteina = combi?.proteina || '';
  const complemento = combi?.complemento || '';
  if (!roll) {
    return [textura, proteina, complemento].filter(Boolean).join(', ');
  }
  const parts = [textura, roll];
  if (isRollEspeciales(roll) && proteina) parts.push(proteina);
  if (complemento) parts.push(complemento);
  const toppings = [];
  if (combi?.topping_crunch) toppings.push('topping crunch');
  if (combi?.topping_ensalada) toppings.push('topping ensalada');
  if (toppings.length) parts.push(toppings.join(' y '));
  return parts.filter(Boolean).join(', ');
}

/**
 * @param {unknown} options
 * @returns {string[]}
 */
export function mapAdditionalOptionNames(options) {
  if (!Array.isArray(options)) return [];
  return options
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (item && typeof item === 'object') {
        return String(item.name || item.additionalName || '').trim();
      }
      return '';
    })
    .filter(Boolean);
}

/** Número de rolls por combinación (cada combinación = 10 rolls) */
export const ROLLS_POR_COMBINACION = 10;

/** Nombre visible de cada grupo de 10 rolls, en el orden del combo. */
const ETIQUETAS_GRUPOS_ROLLS = [
  'TUS PRIMEROS 10 ROLLS',
  'TUS SEGUNDOS 10 ROLLS',
  'TUS TERCEROS 10 ROLLS',
  'TUS CUARTOS 10 ROLLS',
  'TUS QUINTOS 10 ROLLS',
];

/**
 * @param {number} index Índice de la combinación, empezando en 0.
 * @returns {string}
 */
export function etiquetaGrupoRolls(index) {
  const label = ETIQUETAS_GRUPOS_ROLLS[index];
  if (label) return label;
  return `GRUPO ${index + 1} DE 10 ROLLS`;
}

/**
 * Calcula cuántas combinaciones hay según rolls_count (múltiplos de 10).
 * @param {number} rollsCount
 * @returns {number}
 */
export function getNumCombinaciones(rollsCount) {
  if (!rollsCount || rollsCount < ROLLS_POR_COMBINACION) return 0;
  return Math.floor(rollsCount / ROLLS_POR_COMBINACION);
}

/**
 * Inicializa un array de combinaciones vacías (el usuario elige en los selects).
 * @param {number} num
 * @returns {{ textura: string, roll: string, roll_product_id: number|null, proteina: string, complemento: string }[]}
 */
export function getCombinacionesIniciales(num) {
  return Array.from({ length: num }, () => ({
    textura: '',
    roll: '',
    roll_product_id: null,
    proteina: '',
    complemento: '',
    topping_crunch: false,
    topping_ensalada: false,
  }));
}

/** A partir de esta cantidad el cliente puede saltar al armado de la casa. */
export const ROLLS_MODO_CASA_MIN = 40;

export const CASA_TEXTURA = 'COMBINADO';
export const CASA_ROLL = 'MIXTOS';
export const CASA_COMPLEMENTO = 'CON TODO';

export function esComboGrande(rollsCount) {
  return Number(rollsCount) >= ROLLS_MODO_CASA_MIN;
}

/**
 * Todas las piezas de 10 rolls: textura COMBINADO, roll MIXTOS, complemento CON TODO.
 * @param {number} num
 */
export function getCombinacionesDeLaCasa(num) {
  return Array.from({ length: num }, () => ({
    textura: CASA_TEXTURA,
    roll: CASA_ROLL,
    roll_product_id: null,
    proteina: '',
    complemento: CASA_COMPLEMENTO,
    topping_crunch: false,
    topping_ensalada: false,
  }));
}

function normalizeComboName(name) {
  return String(name || '')
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Textura que no se ofrece en ningún combo. */
export function isTexturaCombinado(name) {
  return normalizeComboName(name).includes('COMBINADO');
}

export function texturasSinCombinado(texturas) {
  if (!Array.isArray(texturas)) return [];
  return texturas.filter((name) => !isTexturaCombinado(name));
}

/**
 * Combos de rolls en los que el cliente elige hasta 2 salsas, incluida Fuji.
 * Súper Torta y Full ya traen Fuji en el catálogo y permiten más de 2.
 */
export function comboSeleccionaDosSalsasConFuji(name) {
  const n = normalizeComboName(name);
  return (
    n.includes('PEQUEN') ||
    n.includes('COMBITO') ||
    n.includes('INDIVIDUAL') ||
    n.includes('PAREJA')
  );
}

/** Producto Xetux de la salsa Fuji incluida en la promoción del combo. */
export const FUJI_INCLUDED_SAUCE = {
  id: 'included-fuji-125',
  product_name: 'FUJI',
  default_quantity: 0,
  xetux_product_id: 125,
  xetux_item_id: 125,
  xetux_family_id: 24,
};

export function saucesConFuji(sauces, comboName) {
  const list = Array.isArray(sauces) ? [...sauces] : [];
  if (!comboSeleccionaDosSalsasConFuji(comboName)) return list;
  const hasFuji = list.some((sauce) =>
    normalizeComboName(sauce?.product_name).includes('FUJI')
  );
  if (!hasFuji) list.push({ ...FUJI_INCLUDED_SAUCE });
  return list;
}

export function maxSalsasDelCombo(comboName, apiMax) {
  if (comboSeleccionaDosSalsasConFuji(comboName)) return 2;
  const max = Number(apiMax);
  return Number.isFinite(max) && max > 0 ? max : 1;
}

/** Combo Full (50 rolls): id histórico del catálogo local */
export const COMBO_FULL_ID = 10;

/** Opciones de topping obligatorias solo para Combo Full */
export const COMBO_FULL_TOPPING_OPTIONS = [
  {
    id: 'full-20-crunch-30-dinamita',
    label: '20 rolls con topping crunch y 30 con ensalada dinamita',
    extraEur: 0,
  },
  {
    id: 'full-10-crunch-40-dinamita',
    label: '10 rolls con topping crunch y 40 con ensalada dinamita',
    extraEur: 0,
  },
  {
    id: 'full-all-crunch-2eur',
    label: 'Todos los rolls con topping crunch',
    extraEur: 0,
  },
];

/** Id de la opción de topping por defecto (primera de COMBO_FULL_TOPPING_OPTIONS) */
export const COMBO_FULL_DEFAULT_TOPPING_ID =
  COMBO_FULL_TOPPING_OPTIONS[0]?.id ?? '';

/** Combo Pareja (20 rolls): id fijo en catálogo */
export const COMBO_PAREJA_ID = 11;

/** Opciones de topping solo para Combo Pareja */
export const COMBO_PAREJA_TOPPING_OPTIONS = [
  {
    id: 'pareja-10-crunch-10-dinamita',
    label: '10 Rolls con topping crunch y 10 con ensalada dinamita',
    extraEur: 0,
  },
  {
    id: 'pareja-all-crunch-1eur',
    label: 'Todos los rolls con topping crunch',
    extraEur: 0,
  },
];

/** Id de topping por defecto para Combo Pareja */
export const COMBO_PAREJA_DEFAULT_TOPPING_ID =
  COMBO_PAREJA_TOPPING_OPTIONS[0]?.id ?? '';

/** Pequeñín (10 rolls): id fijo en catálogo */
export const COMBO_PEQUENIN_ID = 9;

/** Combo Individual (10 rolls): id fijo en catálogo */
export const COMBO_INDIVIDUAL_ID = 12;

/** Opciones de topping solo para Combo Individual */
export const COMBO_INDIVIDUAL_TOPPING_OPTIONS = [
  {
    id: 'individual-10-crunch',
    label: '10 Rolls con topping crunch',
    extraEur: 0,
  },
  {
    id: 'individual-10-dinamita',
    label: '10 Rolls con ensalada dinamita',
    extraEur: 0,
  },
];

/** Id de topping por defecto para Combo Individual */
export const COMBO_INDIVIDUAL_DEFAULT_TOPPING_ID =
  COMBO_INDIVIDUAL_TOPPING_OPTIONS[0]?.id ?? '';

const FULL_TOPPING_CONFIG = {
  options: COMBO_FULL_TOPPING_OPTIONS,
  defaultId: COMBO_FULL_DEFAULT_TOPPING_ID,
};

const PAREJA_TOPPING_CONFIG = {
  options: COMBO_PAREJA_TOPPING_OPTIONS,
  defaultId: COMBO_PAREJA_DEFAULT_TOPPING_ID,
};

const INDIVIDUAL_TOPPING_CONFIG = {
  options: COMBO_INDIVIDUAL_TOPPING_OPTIONS,
  defaultId: COMBO_INDIVIDUAL_DEFAULT_TOPPING_ID,
};

/**
 * Toppings solo en Full, Pareja e Individual.
 * Se identifica por nombre: los ids históricos (10, 11, 12) no coinciden
 * con el catálogo actual y hacían aparecer toppings en Súper Torta.
 *
 * @param {number|string|{ id?: number, combo_id?: number, name?: string }} comboOrId
 * @returns {{ options: { id: string, label: string, extraEur: number }[], defaultId: string } | null}
 */
export function getRollsComboToppingConfig(comboOrId) {
  const isObject = comboOrId && typeof comboOrId === 'object';
  const name = normalizeComboName(isObject ? comboOrId.name : '');
  const id = Number(isObject ? (comboOrId.id ?? comboOrId.combo_id) : comboOrId);

  if (name.includes('SUPER TORTA')) return null;
  if (name.includes('FULL')) return FULL_TOPPING_CONFIG;
  if (name.includes('PAREJA')) return PAREJA_TOPPING_CONFIG;
  if (name.includes('INDIVIDUAL')) return INDIVIDUAL_TOPPING_CONFIG;

  if (name) return null;
  if (id === COMBO_FULL_ID || id === 17) return FULL_TOPPING_CONFIG;
  if (id === COMBO_PAREJA_ID || id === 18) return PAREJA_TOPPING_CONFIG;
  if (id === 19) return INDIVIDUAL_TOPPING_CONFIG;
  return null;
}
